import { useId } from 'react';
import { NumberInput } from '../ui/NumberInput';
import { Toggle } from '../ui/Toggle';
import { Spinner } from '../../ui/Spinner';
import { btnSecondary, inputCls } from '../ui/styles';
import { money } from '../../core/format/currency';
import { toCents } from '../../core/format/currency';
import { methodLabel } from './kassaLabels';
import type { PayWith, SaleComposerControl } from './useSaleComposer';
const options: {
  value: PayWith;
  label: string;
}[] = [{
  value: 'pin',
  label: methodLabel.pin
}, {
  value: 'contant',
  label: methodLabel.contant
}, {
  value: 'split',
  label: 'Pin + contant'
}];

/** Wat er te betalen is, een cadeaubon eraf, en hoe: pin, contant (met wisselgeld) of gesplitst. */
export function PaymentBox({
  c
}: {
  c: SaleComposerControl;
}) {
  const id = useId();
  const voucherError = c.voucherError ?? c.fieldErrors.voucherCode ?? null;
  const cashError = c.fieldErrors.cashReceivedCents ?? null;
  const splitError = c.fieldErrors.cashPartCents ?? null;
  return <div className="space-y-5" data-id="element-2411" data-mp="16ppsacipv7tc">
      <div data-id="element-2412" data-mp="16qr6gpv14afa">
        <label htmlFor={`${id}-voucher`} className="mb-1 block text-[12px] text-muted" data-id="element-2413" data-mp="16r0wgdg2qhkz">
          Cadeaubon <span className="text-muted" data-id="element-2414" data-mp="1boj84blh7awo">(optioneel)</span>
        </label>
        <div className="flex flex-wrap gap-2" data-id="element-2415" data-mp="1fotcjjtmdt9f">
          <input id={`${id}-voucher`} value={c.voucherCode} onChange={e => c.changeVoucherCode(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void c.checkVoucher();
          }
        }} placeholder="BB-XXXX-XXXX-XXXX" autoComplete="off" spellCheck={false} aria-invalid={voucherError ? true : undefined} aria-describedby={voucherError ? `${id}-voucher-error` : undefined} className={`${inputCls} uppercase sm:max-w-[14rem]`} data-id="element-2416" data-mp="1fs7h8ypraek1" />
          <button type="button" onClick={() => void c.checkVoucher()} disabled={!c.voucherCode.trim() || c.checkingVoucher} className={btnSecondary} data-id="element-2417" data-mp="2deobtw73ue23">
            {c.checkingVoucher && <Spinner data-id="element-2418" data-mp="8ck8lycx99ux" />}
            Controleren
          </button>
        </div>
        {voucherError ? <p id={`${id}-voucher-error`} role="alert" className="mt-1.5 text-[12px] text-clay" data-id="element-2419" data-mp="n1ox1xab8abh">
            {voucherError}
          </p> : c.voucher && <p className="mt-1.5 text-[12px] text-muted" data-id="element-2420" data-mp="w1v2qwzro603">
              Bon …{c.voucher.last4} · saldo {money(c.voucher.balanceCents)}
              {c.voucher.validFor === 'treatments' && ' · alleen behandelingen'}
            </p>}
      </div>

      {c.creditBalanceCents > 0 && <div className="sm:max-w-md" data-id="element-2421" data-mp="110y8ykrhgi9n">
          <Toggle checked={c.creditOn && c.creditAvailableCents > 0} onChange={on => c.setCreditOn(on && c.creditAvailableCents > 0)} label={`Tegoed gebruiken · saldo ${money(c.creditBalanceCents)}`} description={c.creditAvailableCents > 0 ? c.creditAvailableCents < c.creditBalanceCents ? `Er gaat ${money(c.creditAvailableCents)} af; ${money(c.creditBalanceCents - c.creditAvailableCents)} blijft op haar tegoed.` : 'Het hele tegoed gaat eraf.' : 'Op deze bon staat niets waarop tegoed kan (een cadeaubon telt niet).'} data-id="element-2422" data-mp="111znx0bmdown" />
          {c.fieldErrors.creditCents && <p role="alert" className="mt-1.5 text-[12px] text-clay" data-id="element-2423" data-mp="zihh6ggb23nq">
              {c.fieldErrors.creditCents}
            </p>}
        </div>}

      <dl className="space-y-1.5 border-t border-line pt-4 text-[14px]" data-id="element-2424" data-mp="pmehricu8j4h">
        <div className="flex justify-between gap-3" data-id="element-2425" data-mp="ukpsrwevczoa">
          <dt className="text-muted" data-id="element-2426" data-mp="ul7v9as4ka4n">Totaal</dt>
          <dd className="tabular-nums text-ink" data-id="element-2427" data-mp="2jzvv6cjj3zls">{money(c.totalCents)}</dd>
        </div>
        {c.voucherCents > 0 && <div className="flex justify-between gap-3" data-id="element-2428" data-mp="2oyz3q6ypwnyi">
            <dt className="text-muted" data-id="element-2429" data-mp="2owcvs67x06fr">Cadeaubon</dt>
            <dd className="tabular-nums text-ink" data-id="element-2430" data-mp="ovg32f79efiy">−{money(c.voucherCents)}</dd>
          </div>}
        {c.creditCents > 0 && <div className="flex justify-between gap-3" data-id="element-2431" data-mp="ksrr3yn90shr">
            <dt className="text-muted" data-id="element-2432" data-mp="fuq97h1k6z7v">Tegoed</dt>
            <dd className="tabular-nums text-ink" data-id="element-2433" data-mp="fxm7x7l8drxd">−{money(c.creditCents)}</dd>
          </div>}
        <div className="flex items-baseline justify-between gap-3 pt-1" data-id="element-2434" data-mp="az2igx7fhy09">
          <dt className="text-ink" data-id="element-2435" data-mp="5z6hz7xxwopg">Te betalen</dt>
          <dd className="font-display text-[1.9rem] font-light leading-none tabular-nums text-ink" data-id="element-2436" data-mp="16daicb3729o">{money(c.toPayCents)}</dd>
        </div>
      </dl>

      {c.toPayCents > 0 && <fieldset data-id="element-2437" data-mp="3obl2c4sk158r">
          <legend className="mb-2 text-[12px] text-muted" data-id="element-2438" data-mp="3oc4k9li7ftms">Betaald met</legend>
          <div className="grid grid-cols-3 gap-2 sm:max-w-md" data-id="element-2439" data-mp="3jci7fd6agfei">
            {options.map(o => <button key={o.value} type="button" aria-pressed={c.payWith === o.value} onClick={() => c.setPayWith(o.value)} className={`whitespace-nowrap rounded-md border px-3 py-2.5 text-[14px] transition-colors duration-150 ease-soft ${c.payWith === o.value ? 'border-ink bg-ink text-shell' : 'border-line bg-white text-ink hover:border-ink/40'}`} data-id="element-2440" data-mp="3edf0fn6qzz3w">
                {o.label}
              </button>)}
          </div>
          {c.payWith === 'split' && <div className="mt-3 flex flex-wrap items-end gap-4" data-id="element-2441" data-mp="2qi3axfay3wvg">
              <label className="block" data-id="element-2442" data-mp="2qid2h7bn2c39">
                <span className="mb-1 block text-[12px] text-muted" data-id="element-2443" data-mp="2vhg5ynbz0a94">Contant deel (€)</span>
                <NumberInput decimals allowEmpty value={c.cashPartCents === undefined ? undefined : c.cashPartCents / 100} onChange={v => c.setCashPartCents(v === undefined ? undefined : toCents(v))} className="w-32" data-id="element-2444" data-mp="307wlcczcq5y4" />
              </label>
              <p className="pb-2 text-[14px] text-ink" aria-live="polite" data-id="element-2445" data-mp="1zpl7w459v05k">
                Pinnen <span className="tabular-nums" data-id="element-2446" data-mp="1uqrv6dxt7d6g">{money(c.pinPartCents)}</span>
              </p>
            </div>}
          {c.payWith === 'pin' ? <p className="mt-2 text-[12px] text-muted" data-id="element-2447" data-mp="24hmi7kivqgsy">Pin eerst op de pinautomaat. Reken hier af als de betaling gelukt is.</p> : c.cashDue > 0 && <div className="mt-3 flex flex-wrap items-end gap-4" data-id="element-2448" data-mp="1gsa8mjjn46q4">
                <label className="block" data-id="element-2449" data-mp="1bu0co6q3d23g">
                  <span className="mb-1 block text-[12px] text-muted" data-id="element-2450" data-mp="1bvbiec33kjx7">{c.payWith === 'split' ? 'Contant ontvangen (€)' : 'Ontvangen (€)'}</span>
                  <NumberInput decimals allowEmpty value={c.cashCents === undefined ? undefined : c.cashCents / 100} onChange={v => c.setCashCents(v === undefined ? undefined : toCents(v))} className="w-32" data-id="element-2451" data-mp="1lqvaenejiygo" />
                </label>
                <p className="pb-2 text-[14px] text-ink" aria-live="polite" data-id="element-2452" data-mp="34s5phndvuakn">
                  Wisselgeld <span className="tabular-nums" data-id="element-2453" data-mp="39mt48uj33qm1">{money(c.change)}</span>
                </p>
              </div>}
          {c.payWith === 'split' && <p className="mt-2 text-[12px] text-muted" data-id="element-2454" data-mp="2zrrf3yot6ojg">Pin het restbedrag op de pinautomaat. Reken hier af als beide binnen zijn.</p>}
          {splitError && <p role="alert" className="mt-1.5 text-[12px] text-clay" data-id="element-2455" data-mp="3sfdt80vnxbkk">
              {splitError}
            </p>}
          {cashError && <p role="alert" className="mt-1.5 text-[12px] text-clay" data-id="element-2456" data-mp="3en7f0kc2lenr">
              {cashError}
            </p>}
        </fieldset>}
    </div>;
}